import { useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { QualificationFormField } from "../components/custom-tool-fields";
import { BookingIntegrationField } from "../components/booking-integration-field";
import { VideoIntegrationField } from "../components/video-integration-field";
import { MainPhoneField } from "../components/main-phone-field";
import { SmsAlertField } from "../components/sms-alert-field";
import { MessageCategoriesField } from "../components/message-categories-field";
import type { CustomFieldProps, FieldGroup } from "../components/dynamic-form";
import type { Tool } from "@/features/tools/types";

/**
 * Custom hook to get tool-specific field configurations, visibility rules, and groupings
 * Used for rendering dynamic forms with custom fields for different tools
 */
export function useToolConfiguration(tool: Tool | undefined) {
  const { t, i18n } = useTranslation("tools");
  const language = i18n.language;

  return useMemo(() => {
    const toolName = tool?.name;

    // Section headings are ours, not the backend's: they live in the locale
    // files beside the tool's catalogue entry.
    const group = (key: string, fields: string[]): FieldGroup => {
      const base = `tools.catalog.${toolName}.groups.${key}`;
      return {
        title: t(`${base}.title`),
        description:
          t(`${base}.description`, { defaultValue: "" }) || undefined,
        fields,
      };
    };

    // Qualifications tool customization
    if (toolName === "qualifications") {
      return {
        customFields: {
          form_ids: (props: CustomFieldProps) => (
            <QualificationFormField {...props} />
          ),
        },
        fieldVisibility: {
          integration_key: (data: Record<string, unknown>) =>
            data.integration !== "internal",
        },
        fieldGroups: [
          group("integration", ["integration", "integration_key"]),
          group("qualification", ["form_ids", "request_callback_preferences"]),
        ],
      };
    }

    // Booking tool customization
    if (toolName === "booking") {
      // Each integration's account (URL, login, password, token) is an external
      // account, connected and checked in its own configurator and picked through
      // the integration field (`booking_account_id`): never a field of this form.
      const inConfigurator = () => false;

      return {
        customFields: {
          integration: (props: CustomFieldProps) => (
            <BookingIntegrationField {...props} />
          ),
          // Video meetings are an integration too: none, or a Google Meet account.
          video_account_id: (props: CustomFieldProps) => (
            <VideoIntegrationField {...props} />
          ),
        },
        fieldVisibility: {
          booking_account_id: inConfigurator,
          // The fallback account only means something once video links are on.
          // Set by the video integration field, with the account.
          enable_video_link: inConfigurator,
          online_booking_url: (data: Record<string, unknown>) =>
            data.enable_online_booking_fallback === true,
        },
        fieldGroups: [
          // The picked integration's account is summed up under the picker, in the
          // same card: it is the second half of the same choice.
          group("integration", [
            "integration",
            // Hidden, but grouped: an ungrouped field would land in "other settings".
            "booking_account_id",
          ]),
          group("video", ["enable_video_link", "video_account_id"]),
          group("onlineBooking", [
            "enable_online_booking_fallback",
            "online_booking_url",
          ]),
          group("chatBooking", ["chat_booking_verification"]),
          group("notifications", [
            "enable_reminders",
            "enable_confirmations",
            "notify_performers_by_email",
          ]),
          group("booking", [
            "events_require_confirmation",
            "auto_assign_performer",
            "minimum_notice_hours",
            "minimum_notice_days",
            "allow_updates_before_hours",
            "allow_cancellation_before_hours",
          ]),
        ],
      };
    }

    // Basic tools customization
    if (toolName === "basic_tools") {
      // The delay window and the quotas only mean something once a channel is on.
      const remindersEnabled = (data: Record<string, unknown>) =>
        data.reminder_channels !== "disabled" &&
        data.reminder_channels !== undefined;

      return {
        customFields: {
          main_destination: (props: CustomFieldProps) => (
            <MainPhoneField {...props} />
          ),
        },
        fieldVisibility: {
          // Nothing to verify when the agent cannot transfer at all.
          call_transfer_verification: (data: Record<string, unknown>) =>
            data.enable_call_transfer !== "disabled",
          reminder_min_delay_minutes: remindersEnabled,
          reminder_max_delay_minutes: remindersEnabled,
          reminder_max_pending: remindersEnabled,
          reminder_max_per_day: remindersEnabled,
        },
        fieldGroups: [
          group("calls", [
            "main_destination",
            "enable_call_transfer",
            "call_transfer_verification",
          ]),
          group("knowledge", ["enable_rag_search"]),
          group("reminders", [
            "reminder_channels",
            "reminder_min_delay_minutes",
            "reminder_max_delay_minutes",
            "reminder_max_pending",
            "reminder_max_per_day",
          ]),
        ],
      };
    }

    // Post-call SMS customization
    if (toolName === "post_call_sms") {
      // Each message's own settings only mean something once that message is on.
      const thankYouEnabled = (data: Record<string, unknown>) =>
        data.enable_thank_you === true;
      const followUpEnabled = (data: Record<string, unknown>) =>
        data.enable_follow_up === true;

      return {
        customFields: {},
        fieldVisibility: {
          thank_you_template: thankYouEnabled,
          thank_you_personalization: thankYouEnabled,
          thank_you_cooldown_days: thankYouEnabled,
          follow_up_delay_days: followUpEnabled,
          follow_up_template: followUpEnabled,
          follow_up_personalization: followUpEnabled,
        },
        fieldGroups: [
          group("afterCall", [
            "enable_thank_you",
            "thank_you_template",
            "thank_you_personalization",
            "thank_you_cooldown_days",
          ]),
          group("followUp", [
            "enable_follow_up",
            "follow_up_delay_days",
            "follow_up_template",
            "follow_up_personalization",
          ]),
        ],
      };
    }

    // Messagerie customization
    if (toolName === "messages") {
      return {
        customFields: {
          categories: (props: CustomFieldProps) => (
            <MessageCategoriesField {...props} />
          ),
          sms_alert_destination: (props: CustomFieldProps) => (
            <SmsAlertField {...props} />
          ),
        },
        fieldVisibility: {
          // Only a request being closed with a call back reads it.
          callback_min_delay_minutes: (data: Record<string, unknown>) =>
            data.enable_auto_callback !== false,
        },
        fieldGroups: [
          group("capture", ["topics", "capture", "categories"]),
          group("alert", ["sms_alert_destination"]),
          group("customer", [
            "confirm_by_sms",
            "pending_visibility_days",
            "reply_visibility_days",
          ]),
          group("callback", [
            "enable_auto_callback",
            "callback_min_delay_minutes",
          ]),
        ],
      };
    }

    // Default: no customization
    return {
      customFields: {},
      fieldVisibility: {},
      fieldGroups: undefined,
    };
    // `t` is a new function on every render; the language is what it depends on.
  }, [tool, language]);
}
